'use client'
import * as React from 'react'
import { LazyMotion, domAnimation } from 'framer-motion'
import { tw } from 'utils/classnames'
import { useLegacyProductsListPpeProductsQuery } from 'codegen/generated/cf'
import {
  useHeightResizeObserver,
  useWidthResizeObserver,
} from '@repo/web-ui/hooks/use-resize-observer'
import { keepPreviousData } from '@tanstack/react-query'

import { PreviewProductCard } from './prod-card'
import { ToolsCardMemo } from './feature'
import { Navigation } from './navigation'
import { useMatchMedia } from './use-match-media'
import { useWasDownloadedState } from '@web/app/[locale]/(web)/prod/[slug]/product-asset-context'

export function SuggestionsComp() {
  const [isOpen, setIsOpen] = useWasDownloadedState()
  const [page, setPage] = React.useState(1)
  const height = useHeightResizeObserver(document?.querySelector('[data-id="preview-prod"]'))
  const width = useWidthResizeObserver(document?.querySelector('[data-id="preview-prod"]'))
  const isPortrait = height > width
  const limit = useSuggestionsLimit()
  const { data, isPending } = useLegacyProductsListPpeProductsQuery(
    {
      limit,
      offset: (page - 1) * limit,
    },
    {
      placeholderData: keepPreviousData,
    },
  )
  const hasMore = Boolean(data?.legacyProductsListPPEProducts.meta.nextOffset)

  React.useEffect(() => {
    const previewProd = document.querySelector('[data-id="preview-prod"]')

    if (isOpen) {
      const height = (window?.innerWidth ?? 0) > 1120 ? 'min-height: 32rem;' : 'min-height: 15rem;'
      previewProd?.setAttribute('style', height)
    } else {
      previewProd?.setAttribute('style', 'height: auto;')
    }
  }, [isOpen])

  function onClose() {
    setIsOpen(false)
  }

  if (!isPending && !data?.legacyProductsListPPEProducts?.products.length) {
    return null
  }

  return (
    <div className="bg-cf-white rounded-2 animate-fade-in absolute left-0 top-0 z-[1] m-2 flex h-[calc(100%-1rem)] w-[calc(100%-1rem)] flex-col p-2">
      <Navigation
        setPage={setPage}
        page={page}
        hasMore={isPending || hasMore}
        onClose={onClose}
        className="mb-2 flex items-center"
      />
      <div
        className={tw(
          'grid flex-1 auto-rows-auto gap-2 overflow-auto min-[1500px]:auto-rows-fr',
          isPortrait
            ? 'grid-cols-1 grid-rows-2 min-[575px]:grid-cols-2 min-[1120px]:grid-cols-2 min-[1500px]:grid-cols-3'
            : 'grid-cols-2 grid-rows-1 min-[575px]:grid-rows-2 min-[1120px]:grid-cols-3 min-[1500px]:grid-cols-4',
        )}
      >
        <LazyMotion features={domAnimation}>
          {isPending || !data?.legacyProductsListPPEProducts
            ? [...Array(10)].map((_, index) => <PreviewProductCard key={index} title="" slug="" />)
            : data.legacyProductsListPPEProducts.products.map((product, index) => (
                <PreviewProductCard
                  key={index}
                  title={product.title}
                  image={product.image}
                  slug={product.slug}
                />
              ))}
        </LazyMotion>
        <ToolsCardMemo
          key={page}
          className={isPortrait ? 'col-span-1 min-[575px]:col-span-2' : ''}
        />
      </div>
    </div>
  )
}

export function useSuggestionsLimit() {
  const max1500 = useMatchMedia('(max-width: 1499px)')
  const max1120 = useMatchMedia('(max-width: 1119px)')
  const max575 = useMatchMedia('(max-width: 574px)')

  if (max575) {
    return 2
  }

  if (max1120) {
    return 4
  }

  if (max1500) {
    return 6
  }

  return 10
}
